import React, { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { X, Play, Pause, ArrowLeft, ArrowRight, BookOpen } from '@phosphor-icons/react';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// A5 ratio 1:1.414. Pick a viewport-friendly size that keeps the ratio.
const ratioClamp = (availW, availH) => {
  // Fit a two-page (book) spread: width = 2 * pageW, height = pageH, where pageH = pageW * 1.414
  const maxSpreadW = availW - 40;
  const maxH = availH - 40;
  let pageW = Math.min(maxSpreadW / 2, maxH / 1.414);
  pageW = Math.max(200, Math.min(420, pageW));
  const pageH = pageW * 1.414;
  return { pageW: Math.floor(pageW), pageH: Math.floor(pageH) };
};

const FONT_CSS = {
  serif: '"Playfair Display", Georgia, serif',
  script: '"Dancing Script", "Brush Script MT", cursive',
  sans: '"Inter", system-ui, sans-serif',
  modern: '"Montserrat", "Inter", sans-serif',
};

const Page = forwardRef(({ children, className = '', style = {} }, ref) => (
  <div
    ref={ref}
    className={`flipbook-page bg-white text-[#1a1a2e] overflow-hidden ${className}`}
    style={{
      boxShadow: 'inset 0 0 40px rgba(0,0,0,0.06)',
      ...style,
    }}
  >
    {children}
  </div>
));
Page.displayName = 'FlipBookPage';

const CoverPage = forwardRef(({ eventName, eventDate, count }, ref) => (
  <div
    ref={ref}
    className="flipbook-page overflow-hidden relative text-white"
    style={{
      background: 'linear-gradient(135deg, #1a1a2e 0%, #B89762 100%)',
    }}
  >
    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-4">A Memory Guest Book</div>
      <h2
        className="font-black leading-tight mb-3 px-2"
        style={{ fontFamily: FONT_CSS.serif, fontSize: 'clamp(22px, 5vw, 32px)' }}
      >
        {eventName || 'Our Event'}
      </h2>
      {eventDate && (
        <p className="text-white/80 text-sm mb-4" style={{ fontFamily: FONT_CSS.serif }}>
          {new Date(eventDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      )}
      <div className="w-16 h-[1px] bg-[#C29D52] my-3" />
      <p className="text-white/60 text-xs italic">
        {count > 0 ? `${count} ${count === 1 ? 'message' : 'messages'} from your guests` : 'Be the first to sign'}
      </p>
    </div>
    <div className="absolute bottom-3 left-0 right-0 text-center text-[9px] text-white/40">
      Powered by eventdrop.co
    </div>
  </div>
));
CoverPage.displayName = 'CoverPage';

const EntryPage = forwardRef(({ entry, pageNumber }, ref) => {
  const fontCss = FONT_CSS[entry.font_family] || FONT_CSS.serif;
  const date = entry.created_at
    ? new Date(entry.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : '';

  return (
    <Page ref={ref}>
      <div className="h-full w-full relative flex flex-col p-5 sm:p-6">
        {/* Decorative quote mark for typed entries */}
        {entry.entry_type === 'typed' && (
          <div
            className="absolute top-2 left-4 text-[#e7ecf3] select-none pointer-events-none"
            style={{ fontSize: 56, fontFamily: FONT_CSS.serif, lineHeight: 1 }}
            aria-hidden
          >
            &ldquo;
          </div>
        )}

        <div className="flex-1 flex items-center justify-center overflow-hidden">
          {entry.entry_type === 'drawn' && entry.signature_data_url ? (
            <img
              src={entry.signature_data_url}
              alt={`Signature by ${entry.guest_name}`}
              className="max-w-full max-h-full object-contain"
              draggable={false}
            />
          ) : (
            <p
              className="text-center leading-relaxed text-[#1a1a2e] whitespace-pre-wrap"
              style={{
                fontFamily: fontCss,
                fontSize: 'clamp(14px, 2.5vw, 18px)',
                maxHeight: '100%',
                overflowY: 'auto',
              }}
            >
              {(entry.message_text || '').slice(0, 900)}
            </p>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-[#e7ecf3] flex items-center justify-between">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-[10px] uppercase tracking-wider text-[#9aa0af]">—</span>
            <span className="text-sm font-bold text-[#1a1a2e] truncate" style={{ fontFamily: fontCss }}>
              {entry.guest_name || 'A guest'}
            </span>
          </div>
          <span className="text-[10px] text-[#9aa0af] flex-shrink-0 ml-2">{date}</span>
        </div>

        <div className="absolute bottom-1.5 left-0 right-0 text-center text-[8px] text-[#b8c0d0]">
          {pageNumber} · Powered by eventdrop.co
        </div>
      </div>
    </Page>
  );
});
EntryPage.displayName = 'EntryPage';

const BackCoverPage = forwardRef((_props, ref) => (
  <div
    ref={ref}
    className="flipbook-page overflow-hidden relative text-white flex flex-col items-center justify-center"
    style={{ background: 'linear-gradient(135deg, #B89762 0%, #C29D52 100%)' }}
  >
    <BookOpen size={42} weight="bold" className="mb-3 opacity-80" />
    <p className="text-lg font-bold mb-1" style={{ fontFamily: FONT_CSS.serif }}>
      Thanks for the memories.
    </p>
    <p className="text-white/70 text-xs">Created with love from your guests</p>
    <div className="absolute bottom-3 left-0 right-0 text-center text-[9px] text-white/60">
      Powered by eventdrop.co
    </div>
  </div>
));
BackCoverPage.displayName = 'BackCoverPage';

const FlipBook = ({ slug, onClose, eventNameFallback = 'Event', autoStart = false, hideClose = false, qrUrl = null }) => {
  const [entries, setEntries] = useState([]);
  const [eventName, setEventName] = useState(eventNameFallback);
  const [eventDate, setEventDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dims, setDims] = useState(() => ratioClamp(window.innerWidth, window.innerHeight));
  const [currentPage, setCurrentPage] = useState(0);
  const [autoplay, setAutoplay] = useState(autoStart);
  const bookRef = useRef(null);

  // Load entries
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/events/${slug}/guestbook/flipbook`);
        if (!res.ok) throw new Error('Could not load guestbook');
        const data = await res.json();
        if (cancelled) return;
        setEntries(data.entries || []);
        if (data.event_name) setEventName(data.event_name);
        if (data.event_date) setEventDate(data.event_date);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Live refresh — poll every 15s while autoplay is on so new guest entries
  // appear in the book without a manual reload (venue-TV mode). Cheap public GET.
  useEffect(() => {
    if (!autoplay) return;
    const iv = setInterval(async () => {
      try {
        const res = await fetch(`${API}/events/${slug}/guestbook/flipbook`);
        if (!res.ok) return;
        const data = await res.json();
        setEntries(prev => (data.entries && data.entries.length !== prev.length) ? data.entries : prev);
      } catch { /* silent */ }
    }, 15000);
    return () => clearInterval(iv);
  }, [autoplay, slug]);

  // Responsive resizing
  useEffect(() => {
    const onResize = () => setDims(ratioClamp(window.innerWidth, window.innerHeight));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Escape to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && onClose) onClose();
      if (e.key === 'ArrowRight') bookRef.current?.pageFlip()?.flipNext();
      if (e.key === 'ArrowLeft') bookRef.current?.pageFlip()?.flipPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Autoplay loop (great for venue screens)
  useEffect(() => {
    if (!autoplay) return;
    const iv = setInterval(() => {
      const flip = bookRef.current?.pageFlip();
      if (!flip) return;
      const pages = flip.getPageCount?.() || 0;
      const page = flip.getCurrentPageIndex?.() || 0;
      if (page >= pages - 1) flip.flipTo?.(0); else flip.flipNext();
    }, 6000);
    return () => clearInterval(iv);
  }, [autoplay]);

  const handleFlip = useCallback((e) => setCurrentPage(e.data), []);
  const next = () => bookRef.current?.pageFlip()?.flipNext();
  const prev = () => bookRef.current?.pageFlip()?.flipPrev();
  const jumpToLatest = () => {
    const flip = bookRef.current?.pageFlip();
    const pages = flip?.getPageCount?.() || 1;
    flip?.flipTo?.(pages - 1);
  };

  const { pageW, pageH } = dims;
  const totalPages = entries.length + 2; // cover + entries + back

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-black/90 backdrop-blur-sm"
      data-testid="flipbook-overlay"
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-10">
        <div className="text-white text-xs sm:text-sm font-semibold truncate flex items-center gap-2">
          <BookOpen size={16} weight="bold" className="flex-shrink-0" />
          <span className="truncate">{eventName} · Guestbook</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoplay(v => !v)}
            className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full text-[11px] font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors"
            data-testid="flipbook-autoplay"
            aria-pressed={autoplay}
          >
            {autoplay ? <><Pause size={12} weight="bold" /> Auto</> : <><Play size={12} weight="bold" /> Auto</>}
          </button>
          <button
            onClick={jumpToLatest}
            className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full text-[11px] font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors"
            data-testid="flipbook-latest"
          >
            Latest
          </button>
          {!hideClose && (
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              data-testid="flipbook-close"
              aria-label="Close"
            >
              <X size={18} weight="bold" />
            </button>
          )}
        </div>
      </div>

      {/* QR overlay for venue / live-book mode — persistent in bottom-left */}
      {qrUrl && (
        <div
          className="absolute bottom-4 left-4 z-10 flex items-center gap-3 px-3 py-2 rounded-2xl bg-white/95 text-[#1a1a2e] shadow-lg"
          data-testid="flipbook-qr"
        >
          <div className="bg-white p-1.5 rounded-lg">
            <QRCodeSVG value={qrUrl} size={74} level="M" bgColor="#ffffff" fgColor="#1a1a2e" />
          </div>
          <div className="max-w-[160px]">
            <div className="text-[10px] uppercase tracking-wider text-[#B89762] font-bold">Add your message</div>
            <div className="text-xs font-bold leading-tight mt-0.5">Scan to sign the Memory Guest Book</div>
            <div className="text-[9px] text-[#1a1a2e]/60 mt-1">Powered by eventdrop.co</div>
          </div>
        </div>
      )}

      {/* Book */}
      <div className="flex items-center justify-center w-full h-full">
        {loading ? (
          <div className="text-white/70 text-sm flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-[#B89762]/45 border-t-transparent rounded-full animate-spin" />
            Loading the book…
          </div>
        ) : error ? (
          <div className="text-white/70 text-sm">Could not load guestbook. Try again.</div>
        ) : (
          <div style={{ width: pageW * 2, maxWidth: '100%' }}>
            <HTMLFlipBook
              ref={bookRef}
              width={pageW}
              height={pageH}
              size="fixed"
              minWidth={200}
              maxWidth={500}
              minHeight={282}
              maxHeight={707}
              maxShadowOpacity={0.4}
              showCover
              mobileScrollSupport
              flippingTime={700}
              onFlip={handleFlip}
              className="mx-auto"
              style={{}}
              data-testid="flipbook"
            >
              {/* Front cover */}
              <CoverPage eventName={eventName} eventDate={eventDate} count={entries.length} />

              {/* Entry pages */}
              {entries.length === 0 ? (
                <Page>
                  <div className="h-full flex items-center justify-center text-center px-6">
                    <p className="text-[#9aa0af] italic" style={{ fontFamily: FONT_CSS.serif }}>
                      No messages yet. Be the first to sign.
                    </p>
                  </div>
                </Page>
              ) : (
                entries.map((e, i) => (
                  <EntryPage key={e.entry_id} entry={e} pageNumber={i + 1} />
                ))
              )}

              {/* Back cover */}
              <BackCoverPage />
            </HTMLFlipBook>
          </div>
        )}
      </div>

      {/* Bottom nav */}
      {!loading && !error && entries.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-3 px-4 py-3">
          <button
            onClick={prev}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            data-testid="flipbook-prev"
            aria-label="Previous page"
          >
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div className="text-white/70 text-xs font-semibold" data-testid="flipbook-page-counter">
            {Math.min(currentPage + 1, totalPages)} / {totalPages}
          </div>
          <button
            onClick={next}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            data-testid="flipbook-next"
            aria-label="Next page"
          >
            <ArrowRight size={18} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
};

export default FlipBook;
